import { CHUNK_SIZE, CHUNK_VOLUME } from "./constants";
import { AIR, type BlockId, type PackedLight } from "./types";

/** x-major index used consistently by storage, meshing and workers. */
export function blockIndex(x: number, y: number, z: number): number {
  return x + CHUNK_SIZE * (z + CHUNK_SIZE * y);
}

function inLocalBounds(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < CHUNK_SIZE;
}

/**
 * Palette-compressed 32³ voxel storage.
 *
 * A uniform chunk owns no 64 KiB index array. The first differing write lazily
 * expands it; writes which make it uniform again collapse it automatically.
 */
export class PaletteSubChunk {
  private paletteValues: BlockId[];
  private indices: Uint16Array | null;
  private counts: number[];
  private lightValues: Uint16Array | null = null;
  private uniformLight: PackedLight = 0;

  constructor(fill: BlockId = AIR) {
    this.paletteValues = [fill];
    this.indices = null;
    this.counts = [CHUNK_VOLUME];
  }

  get uniform(): boolean {
    return this.indices === null;
  }

  get uniformBlock(): BlockId | undefined {
    return this.indices === null ? this.paletteValues[0] : undefined;
  }

  get palette(): readonly BlockId[] {
    return this.paletteValues;
  }

  get indexData(): Uint16Array | null {
    return this.indices;
  }

  get(x: number, y: number, z: number): BlockId {
    if (!inLocalBounds(x) || !inLocalBounds(y) || !inLocalBounds(z)) return AIR;
    if (this.indices === null) return this.paletteValues[0] ?? AIR;
    return this.paletteValues[this.indices[blockIndex(x, y, z)] ?? 0] ?? AIR;
  }

  set(x: number, y: number, z: number, id: BlockId): BlockId {
    if (!inLocalBounds(x) || !inLocalBounds(y) || !inLocalBounds(z)) {
      throw new RangeError("local block coordinate is outside a 32³ sub-chunk");
    }

    const offset = blockIndex(x, y, z);
    if (this.indices === null) {
      const previous = this.paletteValues[0] ?? AIR;
      if (previous === id) return previous;
      this.paletteValues.push(id);
      this.counts = [CHUNK_VOLUME - 1, 1];
      this.indices = new Uint16Array(CHUNK_VOLUME);
      this.indices[offset] = 1;
      return previous;
    }

    const previousIndex = this.indices[offset] ?? 0;
    const previous = this.paletteValues[previousIndex] ?? AIR;
    if (previous === id) return previous;

    let nextIndex = this.paletteValues.indexOf(id);
    if (nextIndex < 0) {
      if (this.paletteValues.length >= 0x1_0000) {
        throw new RangeError("sub-chunk palette exceeds Uint16 capacity");
      }
      nextIndex = this.paletteValues.length;
      this.paletteValues.push(id);
      this.counts.push(0);
    }

    this.indices[offset] = nextIndex;
    this.counts[previousIndex] = (this.counts[previousIndex] ?? 0) - 1;
    this.counts[nextIndex] = (this.counts[nextIndex] ?? 0) + 1;
    if (this.counts[nextIndex] === CHUNK_VOLUME) this.collapse(nextIndex);
    return previous;
  }

  fill(id: BlockId): void {
    this.paletteValues = [id];
    this.counts = [CHUNK_VOLUME];
    this.indices = null;
  }

  getLight(x: number, y: number, z: number): PackedLight {
    if (!inLocalBounds(x) || !inLocalBounds(y) || !inLocalBounds(z)) return 0;
    return this.lightValues?.[blockIndex(x, y, z)] ?? this.uniformLight;
  }

  setLight(x: number, y: number, z: number, light: PackedLight): void {
    if (!inLocalBounds(x) || !inLocalBounds(y) || !inLocalBounds(z)) {
      throw new RangeError("local light coordinate is outside a 32³ sub-chunk");
    }
    const packed = light & 0xffff;
    if (this.lightValues === null && packed === this.uniformLight) return;
    if (this.lightValues === null) {
      this.lightValues = new Uint16Array(CHUNK_VOLUME);
      if (this.uniformLight !== 0) this.lightValues.fill(this.uniformLight);
    }
    this.lightValues[blockIndex(x, y, z)] = packed;
  }

  /** Dense copies are intended for worker transfer and persistence adapters. */
  copyBlocks(): Uint16Array {
    const result = new Uint16Array(CHUNK_VOLUME);
    if (this.indices === null) {
      result.fill(this.paletteValues[0] ?? AIR);
      return result;
    }
    for (let i = 0; i < CHUNK_VOLUME; i += 1) {
      result[i] = this.paletteValues[this.indices[i] ?? 0] ?? AIR;
    }
    return result;
  }

  copyLight(): Uint16Array {
    if (this.lightValues !== null) return this.lightValues.slice();
    const result = new Uint16Array(CHUNK_VOLUME);
    if (this.uniformLight !== 0) result.fill(this.uniformLight);
    return result;
  }

  private collapse(index: number): void {
    const value = this.paletteValues[index] ?? AIR;
    this.paletteValues = [value];
    this.counts = [CHUNK_VOLUME];
    this.indices = null;
  }
}
